import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import Home from "@/pages/Home";
import Liberty from "@/pages/Liberty";
import Partner from "@/pages/Partner";
import Contatti from "@/pages/Contatti";
import Accedi from "@/pages/Accedi";
import VerificaEmail from "@/pages/VerificaEmail";
import RecuperaPassword from "@/pages/RecuperaPassword";
import ResetPassword from "@/pages/ResetPassword";
import AreaPersonale from "@/pages/AreaPersonale";
import Admin from "@/pages/Admin";
import Legali from "@/pages/Legali";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function useLenis() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ autoRaf: true, lerp: 0.09 });
    return () => lenis.destroy();
  }, []);
}

function Protected({ children, admin = false }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="auth-loading">
        <span className="eyebrow">Caricamento…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/accedi" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/area" replace />;
  return children;
}

function Shell() {
  useLenis();
  return (
    <div className="min-h-screen flex flex-col bg-cream text-ink">
      <ScrollToTop />
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/liberty" element={<Liberty />} />
          <Route path="/partner" element={<Partner />} />
          <Route path="/contatti" element={<Contatti />} />
          <Route path="/accedi" element={<Accedi />} />
          <Route path="/verifica-email" element={<VerificaEmail />} />
          <Route path="/recupera-password" element={<RecuperaPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/area" element={<Protected><AreaPersonale /></Protected>} />
          <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
          <Route path="/legali/:slug" element={<Legali />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
      <Toaster position="top-center" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
